import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTask,
  deleteTask,
  getProject,
  listActivityLog,
  listPhaseTasks,
  patchTask,
  type ActivityLogEntry,
  type PhaseSummary,
  type Task,
} from '../lib/api';
import { ProjectColorBar } from '../components/ProjectColorBar';
import { TaskAlertsWidget } from '../components/TaskAlertsWidget';
import { SearchPanel } from '../components/SearchPanel';
import { PhaseDateEditModal } from '../components/PhaseDateEditModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { DragHandle, SortableList, useSortableRow } from '../components/Sortable';
import { ClientUpdatesHeadlines } from '../components/ClientUpdatesHeadlines';
import { ClientUpdateComposeModal } from '../components/ClientUpdateComposeModal';
import { UrgentDot } from '../components/UrgentBadge';
import { projectWeekRange } from '../lib/projectWeeks';

/**
 * /projects/:id — project landing (Build D.2).
 *
 * Shows project identity + counts strip + the phase list. Each phase card
 * carries its number/name, target/actual dates, status pill, and a
 * progress bar tied to `percent_complete` from the server. Click any phase
 * → /projects/:id/phases/:phaseId (D.3 task list).
 *
 * Cross-project alerts widget (overdue/due-this-week tasks filtered to this
 * project) lands in D.4 once D.3 ships the phase task UI.
 *
 * D-121 — `viewMode` prop. ProjectPage is now the unified component tree
 * for both the PM-side Overview (admin) AND the client portal (portal).
 * In portal mode:
 *   - Phases filtered by `is_visible_to_client === 1`
 *   - Tasks: all tasks under a visible phase render (D-131 — no per-task
 *     filter; the task list is the client's full project timeline)
 *   - Linked Client Updates filtered to `is_published === 1`
 *   - All edit affordances hidden: + Add update, + Add task, + Add task in
 *     new group, + note, drag handles, ✕ delete, critical toggle, Edit
 *     dates, complete-checkbox interactivity, "Needs attention" widget
 *   - CountsStrip renders as plain-text counters (no Link) per the
 *     `feedback_portal_shows_overview_counts.md` rule (counts strip stays
 *     on top of portal as a confidence signal)
 *   - Compose modals never mount
 *
 * Memory rationale: `feedback_overview_is_portal_admin_view.md` — same
 * component tree, two viewmodes. Don't build a separate portal component.
 */
export default function ProjectPage({
  viewMode = 'admin',
  projectId,
}: {
  viewMode?: 'admin' | 'portal';
  /** D-122 — when provided (typically by the Cloudflare portal's Portal.tsx
   *  wrapper at HARD #2), overrides the URL-derived `:id` param. Lets the
   *  same component render outside the `/projects/:id` route — e.g. when
   *  the portal mounts at `/:slug` and resolves the slug → UUID before
   *  rendering. Admin mode + the /preview/portal/:id sanity-check route
   *  both leave this undefined and fall through to useParams. */
  projectId?: string;
} = {}) {
  const isPortal = viewMode === 'portal';
  const params = useParams<{ id: string }>();
  const id = projectId ?? params.id;
  const [searchParams] = useSearchParams();
  // D-100 — `?expand=<phaseId>` auto-expands a phase on mount. Used by
  // the alerts widget to deep-link from a critical task back to its
  // phase row, since `/projects/:id/phases/:phaseId` (the old PhasePage
  // route) no longer exists.
  const expandPhaseId = searchParams.get('expand');

  const projectQ = useQuery({
    queryKey: ['projects', 'one', id],
    queryFn: () => getProject(id!),
    enabled: !!id,
  });

  // ALL hooks must be called before the early-return guards. Lifted modal
  // state for the per-phase Edit-dates affordance — single shared modal
  // serves every phase row.
  const [editingPhase, setEditingPhase] = useState<PhaseSummary | null>(null);

  if (projectQ.isLoading) {
    return <div className="p-10 text-sm text-ink-2">Loading…</div>;
  }
  if (projectQ.isError || !projectQ.data) {
    return (
      <div className="p-10 text-sm" style={{ color: 'var(--color-danger)' }}>
        Could not load project.
      </div>
    );
  }

  const { project, phases, counts } = projectQ.data;
  const allSortedPhases = [...phases].sort((a, b) => a.phase_number - b.phase_number);
  // D-121 — phase-level visibility filter in portal mode. Phases flagged
  // `is_visible_to_client = 0` (e.g. internal phases the PM hasn't promoted
  // for client view) disappear entirely from the portal render tree.
  const sortedPhases = isPortal
    ? allSortedPhases.filter((p) => p.is_visible_to_client === 1)
    : allSortedPhases;

  // D-119 — phase 01's target_start_date anchors the project-wide week
  // numbering. Same source the Notes Client Updates feed uses (D-115).
  // When phase 01 has no target_start_date, the phase rows skip the
  // "Wk N → Wk M" label entirely (we don't fall back to anything else
  // since the manual-only policy from D-118 means nobody else writes
  // these dates).
  // D-121 — anchor stays computed off the full unfiltered list so the
  // week numbering is identical between admin and portal views, even if
  // phase 01 isn't client-visible.
  const projectStartDate: string | null =
    allSortedPhases.find((p) => p.phase_number === 1)?.target_start_date ?? null;

  const downstreamCount = editingPhase
    ? allSortedPhases.filter((p) => p.phase_number > editingPhase.phase_number).length
    : 0;

  // D-093 — gray palette mirrors the locked tracker-page standard
  // (Documents D-088, Plans D-089, Permits/Pay Apps D-090, Home).
  const docPagePalette: React.CSSProperties = {
    ['--color-page-bg' as string]: '#F3F4F6',
    ['--color-card' as string]: '#FFFFFF',
    ['--color-section-header' as string]: '#D1D5DB',
    ['--color-border' as string]: '#D1D5DB',
    ['--color-border-strong' as string]: '#9CA3AF',
    ['--color-surface' as string]: '#F3F4F6',
    ['--color-surface-2' as string]: '#E5E7EB',
    background: '#F3F4F6',
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" style={docPagePalette}>
      <ProjectColorBar color={project.color_tag} height={4} />

      <header>
        <div className="px-8 pt-6 pb-5">
          <div className="max-w-6xl mx-auto">
            <h1
              className="font-bold text-ink tracking-tight"
              style={{ fontSize: '32px', lineHeight: 1.1 }}
            >
              {project.name}
            </h1>
            <p className="mt-1 text-ink-2 text-sm num">
              {[
                prettyType(project.project_type),
                project.overall_status,
                project.city_state,
                project.target_completion_date
                  ? `Target ${formatShortDate(project.target_completion_date)}`
                  : null,
                project.construction_value != null
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    }).format(project.construction_value)
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto px-8 py-5">
        <div className="max-w-6xl mx-auto space-y-6">
          <CountsStrip
            counts={counts}
            projectId={project.id}
            viewMode={viewMode}
          />

          {/* SearchPanel is a PM-only operational tool (multi-entity search
              + AI synthesis). Skipped in portal mode. */}
          {!isPortal && <SearchPanel projectId={project.id} />}

          {/* Recent client-published updates. The PM authors these on the
              Notes page's Client Updates tab; they surface here in the same
              order they'll render on the client portal (memory:
              feedback_overview_is_portal_admin_view.md). */}
          <ClientUpdatesHeadlines projectId={project.id} viewMode={viewMode} />

          {/* "Needs attention" widget is a PM-internal operational signal
              (past-due / due-soon / critical tasks). Skipped in portal
              mode — clients don't need to see PM's at-risk list. */}
          {!isPortal && (
            <section>
              <h2 className="text-xs font-semibold tracking-wide uppercase text-ink-2 mb-3 border-b border-border pb-1">
                Needs attention
              </h2>
              <TaskAlertsWidget projectId={project.id} />
            </section>
          )}

          <section>
            <h2 className="text-xs font-semibold tracking-wide uppercase text-ink-2 mb-3 border-b border-border pb-1">
              Phases ({sortedPhases.length})
            </h2>
            {sortedPhases.length === 0 ? (
              <p className="text-sm text-ink-3 italic">
                {isPortal
                  ? 'No phases to show yet.'
                  : 'No phases on this project yet.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {sortedPhases.map((p) => (
                  <PhaseRow
                    key={p.id}
                    phase={p}
                    projectId={project.id}
                    projectStartDate={projectStartDate}
                    onEditDates={() => setEditingPhase(p)}
                    initiallyExpanded={p.id === expandPhaseId}
                    viewMode={viewMode}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {editingPhase && !isPortal && (
        <PhaseDateEditModal
          open={!!editingPhase}
          onOpenChange={(o) => {
            if (!o) setEditingPhase(null);
          }}
          phase={editingPhase}
          projectId={project.id}
          downstreamPhaseCount={downstreamCount}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountsStrip — quick-glance counters across the project's tracked surfaces
// ---------------------------------------------------------------------------

function CountsStrip({
  counts,
  projectId,
  viewMode,
}: {
  counts: Record<string, number>;
  projectId: string;
  /** D-121 — in portal mode each tile renders as plain text inside a
   *  static card (no <Link>). The strip stays at the top of the portal
   *  per memory: feedback_portal_shows_overview_counts.md — counters are
   *  a confidence signal of behind-scenes work volume, NOT a navigation
   *  affordance on the client side. */
  viewMode: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  const items: Array<{ label: string; key: string; href: string }> = [
    { label: 'Notes', key: 'notes', href: `/projects/${projectId}/notes` },
    { label: 'Tasks', key: 'tasks', href: `/projects/${projectId}` },
    { label: 'Documents', key: 'tracked_documents', href: `/projects/${projectId}/documents-tracker` },
    { label: 'Plans', key: 'tracked_plans', href: `/projects/${projectId}/plans-tracker` },
    { label: 'Permits', key: 'permits', href: `/projects/${projectId}/permits` },
    { label: 'Pay Apps', key: 'pay_apps', href: `/projects/${projectId}/pay-apps` },
    { label: 'Updates', key: 'activity_log', href: `/projects/${projectId}/notes?tab=client` },
  ];
  return (
    <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {items.map((it) => {
        const n = counts[it.key] ?? 0;
        const inner = (
          <>
            <p className="text-xs uppercase tracking-wide text-ink-3 font-semibold">
              {it.label}
            </p>
            <p className="text-xl font-semibold num text-ink leading-tight">
              {n}
            </p>
          </>
        );
        return (
          <li key={it.key}>
            {isPortal ? (
              <div
                className="block border border-border rounded-md px-3 py-2"
                style={{ background: 'var(--color-card)' }}
              >
                {inner}
              </div>
            ) : (
              <Link
                to={it.href}
                // White (var(--color-card)) instead of bg-surface — under
                // D-089 gray palette, bg-surface resolves to page-bg gray
                // and would blend into the page. White elevates the card.
                // Hover deepens to surface-2 (gray-200) for clear feedback.
                className="block border border-border rounded-md px-3 py-2 hover:border-border-strong hover:bg-[var(--color-surface-2)] focus:outline-none focus:ring-2 focus:ring-accent"
                style={{ background: 'var(--color-card)' }}
              >
                {inner}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// PhaseRow — bordered card per TRACKER_DESIGN.md, click-through to /phases/:id
// ---------------------------------------------------------------------------

function PhaseRow({
  phase,
  projectId,
  projectStartDate,
  onEditDates,
  initiallyExpanded,
  viewMode,
}: {
  phase: PhaseSummary;
  projectId: string;
  /** D-119 — phase 01's target_start_date, anchors the "Wk N → Wk M"
      project-week range label. When null, the week chip doesn't render. */
  projectStartDate: string | null;
  onEditDates: () => void;
  initiallyExpanded?: boolean;
  /** D-121 — portal mode hides the Edit-dates sidecar and threads down
   *  through ExpandedPhaseBody so the task list runs read-only. The
   *  chevron + click-to-expand affordance is retained so clients can
   *  drill into a phase to see scope. */
  viewMode: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  // D-094 — phase rows expand inline to show task_groups + tasks. Click
  // anywhere on the row toggles expansion. D-099 brought drag-reorder +
  // add-task + critical toggle inline so the Overview is the canonical
  // work surface (PhasePage retired under D-100).
  // D-100 — `initiallyExpanded` lets the alerts widget deep-link to a
  // specific phase via `/projects/:id?expand=<phaseId>`.
  const [expanded, setExpanded] = useState(initiallyExpanded ?? false);

  const targetLabel =
    phase.target_start_date && phase.target_end_date
      ? `${formatShortDate(phase.target_start_date)} → ${formatShortDate(phase.target_end_date)}`
      : phase.target_end_date
        ? `through ${formatShortDate(phase.target_end_date)}`
        : null;
  // D-118 — actual_*_date columns dropped (migration 017). Phase dates are
  // strictly manual via target_start_date / target_end_date now; no second
  // "actual" line, no auto-stamps. Per C's policy: "no automation touches
  // my phase dates, period." Cascade-on-edit (forward only) is the only
  // automation that operates on phase dates, and it's PM-triggered via the
  // Edit dates modal save.
  // D-120 — `durLabel` removed. The Wk N → Wk M chip on the progress
  // bar row carries both span and position; the standalone "X wk"
  // duration label was redundant clutter.

  // D-119 — Project-week range chip. Anchored to phase 01's
  // target_start_date so "Week 1" is always the calendar week of project
  // kickoff. Phase 01 will always span "Wk 1 → Wk N" where N matches its
  // own duration; subsequent phases pick up from wherever they fall in
  // the calendar relative to phase 01. Recomputes live on every render
  // so date edits propagate immediately.
  const weekRange = projectWeekRange(
    phase.target_start_date,
    phase.target_end_date,
    projectStartDate,
  );
  const weekRangeLabel = weekRange
    ? weekRange.start === weekRange.end
      ? `Wk ${weekRange.start}`
      : `Wk ${weekRange.start} → Wk ${weekRange.end}`
    : null;

  return (
    <li>
      <div
        // D-098 — subtle shadow + lift on hover for the phase row.
        // Implies clickability and gives the row a deliberate "you're
        // about to do something" feedback before the expand toggles.
        className="border border-border rounded-md hover:border-border-strong hover:shadow-md focus-within:ring-2 focus-within:ring-accent overflow-hidden transition-shadow"
        style={{ background: 'var(--color-card)' }}
      >
        {/* D-120 — Row structure: [chevron column] [content column] [Edit
            sidecar]. The chevron used to be a 12px `▾` text glyph at the
            start of the content row, which (a) was nearly invisible and
            (b) shifted the status pill right by its width while the
            progress-row content below started flush left, breaking
            vertical alignment between the two rows. Now it lives in its
            own fixed-width left column with a vertical separator, so
            row 1 + row 2 content both start at the same x-offset and
            the expand affordance reads as a deliberate click target. */}
        <div className="flex items-stretch">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center justify-center px-2.5 border-r border-border text-ink-2 hover:text-ink hover:bg-[var(--color-surface-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors flex-shrink-0"
            aria-expanded={expanded}
            aria-controls={`phase-body-${phase.id}`}
            aria-label={expanded ? 'Collapse phase' : 'Expand phase'}
            title={expanded ? 'Click to collapse' : 'Click to expand the phase'}
          >
            <ChevronExpandIcon expanded={expanded} />
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex-1 px-3 py-2 focus:outline-none min-w-0 text-left"
            aria-expanded={expanded}
            aria-controls={`phase-body-${phase.id}`}
          >
            <div className="flex items-center flex-wrap gap-2">
              <PhaseStatusPill status={phase.status} />
              <span className="text-xs num text-ink-3">
                Phase {String(phase.phase_number).padStart(2, '0')}
              </span>
              <span className="text-sm font-semibold text-ink">
                {phase.phase_name}
              </span>
              <span className="flex-1" />
              {/* D-116 — Target date span at text-sm font-semibold; load-
                  bearing data for cascade math, client updates week
                  numbering, and the per-phase Wk N → Wk M chip below.
                  D-120 — duration "X wk" label removed; the Wk N → Wk M
                  chip on the progress row supersedes it (range gives
                  position AND span; duration was redundant clutter). */}
              {targetLabel && (
                <span className="text-sm num font-semibold text-ink">
                  {targetLabel}
                </span>
              )}
            </div>
            {/* D-119 — Progress-bar row. Layout: [Wk N → Wk M] chip on
                the left (project-timeline context for this phase), hefty
                progress bar in the middle, percent on the right. D-120
                row alignment: chevron now in its own column, so this
                row's leftmost element (the Wk chip OR the progress bar
                when no week range exists) lines up cleanly under the
                status pill on the row above. */}
            <div className="mt-2 flex items-center gap-3">
              {weekRangeLabel && (
                <span
                  className="text-[11px] num font-semibold uppercase tracking-wider px-2 py-0.5 rounded flex-shrink-0"
                  style={{
                    background: 'var(--color-section-header)',
                    color: 'var(--color-ink-2)',
                  }}
                  title={
                    weekRange && weekRange.start === weekRange.end
                      ? `This phase spans Week ${weekRange.start} of the project`
                      : `This phase spans Weeks ${weekRange!.start} through ${weekRange!.end} of the project`
                  }
                >
                  {weekRangeLabel}
                </span>
              )}
              <ProgressBar percent={phase.percent_complete} />
              <span className="text-sm num font-semibold text-ink flex-shrink-0">
                {phase.percent_complete}%
              </span>
            </div>
          </button>
          {/* Sidecar Edit-dates button — distinct hit area so clicking it
              doesn't toggle the row's expand state. Opens PhaseDateEditModal
              with the D-062 cascade prompt. D-121 — hidden in portal mode;
              phase-date editing is PM-only. */}
          {!isPortal && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditDates();
              }}
              className="px-3 border-l border-border text-ink-3 hover:text-ink hover:bg-[var(--color-surface-2)] focus:outline-none focus:ring-2 focus:ring-accent text-xs flex items-center gap-1 flex-shrink-0"
              title="Edit phase dates (with optional cascade to downstream phases)"
            >
              <CalendarPencilIcon />
              <span className="hidden sm:inline">Edit dates</span>
            </button>
          )}
        </div>

        {expanded && (
          <ExpandedPhaseBody
            phase={phase}
            projectId={projectId}
            id={`phase-body-${phase.id}`}
            viewMode={viewMode}
          />
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ExpandedPhaseBody — lazy-loaded task list grouped by task_group, with
// click-to-complete confirmation. Single source of task interaction on
// the Overview page (D-100 retired PhasePage entirely — drag-reorder,
// add-task, critical toggle all live inline now).
// ---------------------------------------------------------------------------

function ExpandedPhaseBody({
  phase,
  projectId,
  id,
  viewMode,
}: {
  phase: PhaseSummary;
  projectId: string;
  id: string;
  /** D-121 + D-131 — portal mode: collapse empty groups, filter linked
   *  updates to is_published, hide NewGroupAdder, thread viewMode down
   *  to TaskGroupBlock / InlineTaskRow. Task rows no longer filtered by
   *  per-task visibility — the full timeline is always shown. */
  viewMode: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  const qc = useQueryClient();
  // Lazy-load tasks only when the phase is expanded (this component
  // is only rendered when the parent's expanded state is true).
  const tasksQ = useQuery({
    queryKey: ['tasks', 'phase', phase.id],
    queryFn: () => listPhaseTasks(phase.id),
  });

  // D-109 — load phase-tagged activity_log entries here so we can build
  // a task_id → updates map and pass it down to InlineTaskRow. This is
  // the "update → task" rendering direction: when a task has linked
  // updates, the task row surfaces an indicator inline. Per D-110,
  // this is now the ONLY place inside an expanded phase where the
  // user sees their updates — the redundant PhaseUpdatesBlock that
  // duplicated all phase-tagged entries at the top was removed.
  // D-121 — in portal mode, request only published entries from the API
  // so drafts can never leak through to the portal-side linked-update
  // cards.
  const phaseUpdatesQ = useQuery({
    queryKey: [
      'activity-log',
      projectId,
      'phase',
      phase.id,
      isPortal ? 'published' : 'all',
    ],
    queryFn: () =>
      listActivityLog(
        projectId,
        isPortal
          ? { phase_id: phase.id, is_published: 1 }
          : { phase_id: phase.id },
      ),
  });
  const updatesByTaskId = useMemo(() => {
    const m = new Map<string, ActivityLogEntry[]>();
    for (const e of phaseUpdatesQ.data?.entries ?? []) {
      if (e.task_id) {
        if (!m.has(e.task_id)) m.set(e.task_id, []);
        m.get(e.task_id)!.push(e);
      }
    }
    return m;
  }, [phaseUpdatesQ.data?.entries]);

  const grouped = useMemo(() => {
    const tasks = tasksQ.data?.tasks ?? [];
    // D-131 — per-task `is_client_visible` filter removed. The task list
    // is the full project timeline; the client sees every task always.
    // Note gating (linked-update cards, published_note) still applies in
    // portal mode at the row layer — the row exists, the attached note
    // appears only when published.
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      const key = t.task_group || '(Uncategorized)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return Array.from(map.entries()).filter(([, list]) => list.length > 0);
  }, [tasksQ.data]);

  // D-099 — drag-reorder built directly into the inline expanded view.
  // Per-row PATCH of `task_number` (1-based contiguous within the group),
  // optimistic cache update, only changed rows touched. Same proven
  // pattern as PhasePage (D-076), using the cache-shape fix from D-095.
  const reorderMut = useMutation({
    mutationFn: async (writes: Array<{ id: string; task_number: number }>) => {
      for (const w of writes) {
        await patchTask(w.id, { task_number: w.task_number });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phase.id] });
    },
    onError: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phase.id] });
    },
  });

  const makeReorderHandler = (groupTasks: Task[]) => (reordered: Task[]) => {
    // task_number is shared across all groups in a phase (single column).
    // Preserve the per-group base so reorder writes don't collide with
    // task_numbers from sibling groups.
    const base = groupTasks.reduce(
      (min, t) => (t.task_number < min ? t.task_number : min),
      groupTasks[0]?.task_number ?? 1
    );
    const writes: Array<{ id: string; task_number: number }> = [];
    reordered.forEach((t, i) => {
      const desired = base + i;
      if (t.task_number !== desired) {
        writes.push({ id: t.id, task_number: desired });
      }
    });
    if (writes.length === 0) return;

    qc.setQueryData<{ tasks: Task[]; count: number }>(
      ['tasks', 'phase', phase.id],
      (prev) => {
        if (!prev) return prev;
        const map = new Map<string, number>();
        reordered.forEach((t, i) => map.set(t.id, base + i));
        return {
          ...prev,
          tasks: prev.tasks.map((t) =>
            map.has(t.id)
              ? ({ ...t, task_number: map.get(t.id)! } as Task)
              : t
          ),
        };
      }
    );

    reorderMut.mutate(writes);
  };

  return (
    <div
      id={id}
      className="border-t px-4 py-4 space-y-3"
      style={{
        borderColor: 'var(--color-border)',
        background: 'var(--color-surface-2)',
      }}
    >
      {/* D-110 — PhaseUpdatesBlock removed. Per C's "2 places only" rule,
          updates render in: (1) Recent Updates at the top of Overview
          (global feed), and (2) inline under the linked task (specific
          context). The redundant "Updates" block at the top of every
          expanded phase was the confusing third place. Phase-tagged
          updates with no task link only appear in Recent Updates. */}

      {tasksQ.isLoading && (
        <p className="text-xs text-ink-3">Loading tasks…</p>
      )}
      {tasksQ.isError && (
        <p className="text-xs" style={{ color: 'var(--color-danger)' }}>
          Could not load tasks for this phase.
        </p>
      )}
      {!tasksQ.isLoading && grouped.length === 0 && (
        <p className="text-xs text-ink-3 italic">
          {isPortal
            ? 'No tasks to show in this phase yet.'
            : 'No tasks in this phase yet.'}
        </p>
      )}
      {grouped.map(([group, tasks]) => (
        <TaskGroupBlock
          key={group}
          groupName={group}
          tasks={tasks}
          projectId={projectId}
          phaseId={phase.id}
          phaseName={phase.phase_name}
          updatesByTaskId={updatesByTaskId}
          onReorder={makeReorderHandler(tasks)}
          viewMode={viewMode}
        />
      ))}

      {/* D-099 — inline "+ Add task in a new group" affordance at the
          bottom. Mirrors the previous NewGroupAdder pattern so the
          inline view has full feature parity for managing task_groups.
          D-100: PhasePage retired; this is the canonical task work
          surface, so there's no longer an "Open phase view" link
          here (it kept getting accidentally triggered).
          D-121 — hidden in portal mode (no task authoring on the
          client portal). */}
      {!isPortal && (
        <NewGroupAdder
          phaseId={phase.id}
          projectId={projectId}
          existingGroups={grouped.map(([g]) => g)}
        />
      )}
    </div>
  );
}

function TaskGroupBlock({
  groupName,
  tasks,
  projectId,
  phaseId,
  phaseName,
  updatesByTaskId,
  onReorder,
  viewMode,
}: {
  groupName: string;
  tasks: Task[];
  projectId: string;
  phaseId: string;
  phaseName: string;
  updatesByTaskId: Map<string, ActivityLogEntry[]>;
  onReorder: (reordered: Task[]) => void;
  /** D-121 — portal mode hides the "+ Add task" button and renders rows
   *  via InlineTaskRow in read-only mode. Drag-reorder primitives are
   *  retained around the list (cheap) but the DragHandle is suppressed
   *  on each row, so no drag can fire. */
  viewMode: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  const [adding, setAdding] = useState(false);
  const completeCount = tasks.filter((t) => t.is_complete === 1).length;
  const groupPercent = tasks.length > 0
    ? Math.round((completeCount / tasks.length) * 100)
    : 0;
  const allDone = completeCount === tasks.length && tasks.length > 0;

  return (
    <div
      className="rounded-md border overflow-hidden shadow-sm"
      // D-097 / D-098 — task_group rendered as a white card with light
      // border + subtle shadow for clear visual grouping against the
      // surface-2 expanded-body background. Per-group mini progress bar
      // mirrors the phase progress-bar pattern, giving you a quick scan
      // of which groups are done vs. in flight. The shadow adds a hint
      // of elevation so the cards feel "lifted" from the surface
      // beneath them.
      style={{
        background: 'var(--color-card)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {/* Status indicator: green check when fully complete, neutral dot
            when in flight, faint dot when none done. Quick "is this done?"
            scan without reading the count. */}
        <span
          aria-hidden
          className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
          style={
            allDone
              ? { background: 'var(--color-success)' }
              : completeCount > 0
                ? { background: 'var(--color-warning-soft)', border: '1.5px solid var(--color-warning)' }
                : { background: 'var(--color-surface-2)', border: '1.5px solid var(--color-border-strong)' }
          }
        >
          {allDone && (
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3}
                 strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>

        {/* D-100 — group title bumped from text-xs → text-sm + count from
            text-[11px] → text-xs to match the row-level size bump. */}
        <h3 className="text-sm uppercase tracking-[0.08em] font-semibold text-ink flex-1 min-w-0 truncate">
          {groupName}
        </h3>

        {/* Count + percent — tabular figures keep them aligned across stacked groups */}
        <span className="num text-xs text-ink-3 flex-shrink-0">
          {completeCount}/{tasks.length}
        </span>

        {/* Mini progress bar — same vocabulary as the phase progress bar
            (full = green, partial = amber, none = light gray). */}
        <div
          className="h-1.5 rounded overflow-hidden flex-shrink-0"
          style={{
            width: '64px',
            background: 'var(--color-surface-2)',
          }}
          aria-hidden
        >
          <div
            style={{
              width: `${groupPercent}%`,
              height: '100%',
              background:
                groupPercent >= 100
                  ? 'var(--color-success)'
                  : groupPercent > 0
                    ? 'var(--color-warning)'
                    : 'transparent',
              transition: 'width 0.2s ease',
            }}
          />
        </div>

        {/* D-099 — inline "+ Add task" affordance per task_group.
            D-100: size-bumped to match the task title scale.
            D-121 — hidden in portal mode. */}
        {!isPortal && (
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-1 focus:ring-accent rounded ml-1"
            style={{ color: 'var(--color-accent)' }}
          >
            {adding ? 'Cancel' : '+ Add task'}
          </button>
        )}
      </div>

      {/* D-099 — drag-reorder lives directly in this inline view via
          SortableList. Each row is a useSortableRow node; drag listeners
          attach only to the dedicated DragHandle on the left of the row,
          so clicking the rest of the row still toggles complete /
          opens description / etc. without triggering a drag.
          D-121 — in portal mode SortableList is replaced by a plain
          <ul> so dnd-kit primitives don't even mount on the client
          side. Saves a few KB of dnd-kit hooks per phase render. */}
      {isPortal ? (
        <ul className="px-2 py-2 space-y-0.5">
          {tasks.map((t) => (
            <InlineTaskRow
              key={t.id}
              task={t}
              projectId={projectId}
              phaseId={phaseId}
              phaseName={phaseName}
              linkedUpdates={updatesByTaskId.get(t.id) ?? []}
              viewMode={viewMode}
            />
          ))}
        </ul>
      ) : (
        <SortableList
          items={tasks}
          getId={(t) => t.id}
          onReorder={onReorder}
        >
          <ul className="px-2 py-2 space-y-0.5">
            {tasks.map((t) => (
              <InlineTaskRow
                key={t.id}
                task={t}
                projectId={projectId}
                phaseId={phaseId}
                phaseName={phaseName}
                linkedUpdates={updatesByTaskId.get(t.id) ?? []}
                viewMode={viewMode}
              />
            ))}
          </ul>
        </SortableList>
      )}

      {adding && !isPortal && (
        <div className="px-2 pb-2">
          <InlineAddTaskForm
            phaseId={phaseId}
            projectId={projectId}
            taskGroup={groupName === '(Uncategorized)' ? null : groupName}
            onDone={() => setAdding(false)}
          />
        </div>
      )}
    </div>
  );
}

function InlineTaskRow({
  task,
  projectId,
  phaseId,
  phaseName,
  linkedUpdates,
  viewMode,
}: {
  task: Task;
  projectId: string;
  phaseId: string;
  phaseName: string;
  linkedUpdates: ActivityLogEntry[];
  /** D-121 — portal mode hides every edit affordance: drag handle, ✕
   *  delete, critical toggle, "+ note", click-to-complete. The complete-
   *  checkbox renders as a non-interactive state indicator and linked
   *  update cards render read-only. */
  viewMode: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  const qc = useQueryClient();
  const [pendingComplete, setPendingComplete] = useState<boolean | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // D-109 — modal state for opening a linked Client Update from this row.
  // D-111 — also used by the "+ note" quick-add flow, where it opens in
  // create mode pre-loaded with this task's phase + task id (one note
  // format across the app; task_note column dropped).
  const [linkedUpdateModalEntry, setLinkedUpdateModalEntry] =
    useState<ActivityLogEntry | null>(null);
  const [createNoteModalOpen, setCreateNoteModalOpen] = useState(false);

  // D-099 — drag-reorder integration. Each row is a sortable node; the
  // DragHandle component handles the listeners. The rest of the row
  // keeps its click-to-complete / click-to-expand affordances unambiguously.
  // D-121 — useSortableRow is hook-stable across viewmode (Rules of Hooks
  // require unconditional calls). In portal mode the parent TaskGroupBlock
  // doesn't wrap rows in <SortableList>, so the hook degrades to a no-op
  // and the DragHandle below is not rendered.
  const { setNodeRef, style, dragHandleProps } = useSortableRow(task.id);

  // D-121 — only published updates are visible to clients. The
  // ExpandedPhaseBody query already filters to is_published=1 when
  // viewMode === 'portal', but we re-filter at the row layer as a
  // defense-in-depth guard so a stale cache entry from an admin pass
  // can never bleed a draft into a portal render.
  const visibleLinkedUpdates = isPortal
    ? linkedUpdates.filter((u) => u.is_published === 1)
    : linkedUpdates;

  const isComplete = task.is_complete === 1;

  const toggleMut = useMutation({
    mutationFn: async (next: boolean) => {
      await patchTask(task.id, { is_complete: next });
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phaseId] });
      await qc.invalidateQueries({ queryKey: ['phases', 'one', phaseId] });
      await qc.invalidateQueries({ queryKey: ['projects', 'one', projectId] });
      await qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const editDateMut = useMutation({
    mutationFn: async (newDateIso: string) => {
      await patchTask(task.id, { completed_date: newDateIso });
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phaseId] });
    },
    onSuccess: () => setEditingDate(false),
  });

  // D-099 — is_critical toggle, works on every task (prebuilt template
  // tasks AND user-created ones). User flagged that prebuilt tasks
  // couldn't be marked critical from the UI; the field has always been
  // PATCHable, just had no toggle in the row.
  const toggleCriticalMut = useMutation({
    mutationFn: async (next: boolean) => {
      await patchTask(task.id, { is_critical: next });
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phaseId] });
      await qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      await deleteTask(task.id);
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phaseId] });
      await qc.invalidateQueries({ queryKey: ['phases', 'one', phaseId] });
      await qc.invalidateQueries({ queryKey: ['projects', 'one', projectId] });
      await qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const hasDescription = !!task.task_description && task.task_description.length > 0;
  const isCritical = task.is_critical === 1;

  return (
    <li
      ref={isPortal ? undefined : setNodeRef}
      style={isPortal ? undefined : style}
      className={isPortal ? undefined : 'touch-none'}
    >
      <div
        className={`flex items-stretch rounded-md transition-colors ${
          isPortal ? '' : 'group hover:bg-[var(--color-surface-2)]'
        }`}
      >
        {/* D-099 — drag handle on the left. Listeners attached only to
            this dedicated handle so clicking the rest of the row still
            toggles complete / expands description without dragging.
            D-121 — hidden in portal mode (no reorder on the client side). */}
        {!isPortal && (
          <DragHandle
            {...dragHandleProps}
            size="sm"
            className="!bg-transparent hover:!bg-[var(--color-surface-2)] !rounded-md flex-shrink-0 self-center"
          />
        )}

        {/* D-098 — left accent rail. Red for critical tasks, transparent
            otherwise (preserves alignment so non-critical rows still
            line up at the same x-coordinate). Self-stretch makes it
            run the full height of the row including any expanded
            description / note blocks below. */}
        <div
          aria-hidden
          className="w-[3px] rounded-full flex-shrink-0 self-stretch my-1"
          style={{
            background: isCritical ? 'var(--color-danger)' : 'transparent',
          }}
        />
        <div className="flex-1 flex items-start gap-2 pl-2 pr-2 py-1.5 min-w-0">
          {/* D-121 — checkbox stays visible in portal mode as a read-only
              status indicator (filled green when complete, empty box
              when not). Rendered as a <span> instead of <button> so it
              can't be clicked through to the mark-complete confirm flow. */}
          {isPortal ? (
            <span
              role="img"
              aria-label={isComplete ? 'Task complete' : 'Task not yet complete'}
              className="flex items-center justify-center w-4 h-4 mt-0.5 border border-border-strong rounded bg-[var(--color-card)] flex-shrink-0"
              style={
                isComplete
                  ? { background: 'var(--color-success)', borderColor: 'var(--color-success)' }
                  : undefined
              }
            >
              {isComplete && (
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3}
                     strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setPendingComplete(!isComplete)}
              disabled={toggleMut.isPending}
              className="flex items-center justify-center w-4 h-4 mt-0.5 border border-border-strong rounded bg-[var(--color-card)] hover:border-ink focus:outline-none focus:ring-2 focus:ring-accent flex-shrink-0 transition-colors"
              aria-label={isComplete ? 'Mark task incomplete' : 'Mark task complete'}
              title={isComplete ? 'Click to mark incomplete' : 'Click to mark complete'}
              style={
                isComplete
                  ? { background: 'var(--color-success)', borderColor: 'var(--color-success)' }
                  : undefined
              }
            >
              {isComplete && (
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3}
                     strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
              <button
                type="button"
                onClick={() => hasDescription && setShowDescription((v) => !v)}
                // D-100 — task title bumped from `text-sm` → `text-base`
                // for better readability inside the expanded view.
                className={`text-left text-base leading-snug focus:outline-none ${
                  hasDescription ? 'hover:underline cursor-help' : 'cursor-default'
                } ${isComplete ? 'text-ink-2 font-normal' : 'text-ink font-medium'} transition-colors`}
                title={hasDescription ? 'Click to expand description' : undefined}
              >
                {task.task_text}
                {isCritical && (
                  <span
                    className="ml-2 inline-flex items-center text-[11px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider align-middle"
                    style={{
                      background: 'var(--color-danger)',
                      color: '#FFFFFF',
                    }}
                  >
                    Critical
                  </span>
                )}
              </button>
              {isComplete && (
                <CompletedDateTag
                  completedDate={task.completed_date}
                  editing={editingDate}
                  onStartEdit={() => setEditingDate(true)}
                  onCancelEdit={() => setEditingDate(false)}
                  onSubmitEdit={(iso) => editDateMut.mutate(iso)}
                  pending={editDateMut.isPending}
                  readOnly={isPortal}
                />
              )}
            </div>
            {showDescription && hasDescription && (
              <div
                // D-100 — description bumped from `text-xs` → `text-sm`
                // alongside the task title bump for readability parity.
                className="mt-1.5 rounded px-2.5 py-1.5 text-sm text-ink-2 leading-relaxed"
                style={{ background: 'var(--color-surface-2)' }}
              >
                {task.task_description}
              </div>
            )}

            {/* D-110 — linked Client Updates. Per the "2 places only"
                rule, this is the ONE place inside an expanded phase that
                surfaces updates. Card visual matches HeadlineRow on
                Overview so the same data reads the same way wherever it
                appears: title (with phase fallback) + date/category
                subline + body excerpt + draft/published label. Click →
                opens the same compose modal used everywhere else, in
                edit mode. D-121 — in portal mode rows are filtered to
                is_published only and rendered as read-only cards (no
                click, no "Draft" label, no "Edited since publish" dot). */}
            {visibleLinkedUpdates.length > 0 && (
              <ul className="mt-1.5 space-y-1.5">
                {visibleLinkedUpdates.map((u) => {
                  const isUrgent = u.is_urgent === 1;
                  const isPublished = u.is_published === 1;
                  const hasUnpublished = u.has_unpublished_changes === 1;
                  const titleText =
                    u.entry_title || `${u.log_type} on ${phaseName}`;
                  // D-112 — three-tint backgrounds. Pure white reads as
                  // flat against the page-card surface; light gray accent
                  // gives published rows subtle separation.
                  const bg = isUrgent
                    ? 'color-mix(in srgb, var(--color-danger-soft) 60%, var(--color-card))'
                    : isPublished
                      ? 'color-mix(in srgb, var(--color-section-header) 18%, var(--color-card))'
                      : 'var(--color-warning-soft)';
                  const border = isUrgent
                    ? 'var(--color-danger)'
                    : 'var(--color-border)';
                  const cardInner = (
                    <>
                      <div className="flex items-center gap-1.5">
                        {isUrgent && <UrgentDot />}
                        <h4 className="text-sm font-semibold text-ink leading-tight flex-1 min-w-0 truncate">
                          {titleText}
                        </h4>
                        {hasUnpublished && !isPortal && (
                          <span
                            className="pub-dot flex-shrink-0"
                            aria-hidden
                            title="Edited since publish"
                          />
                        )}
                        {!isPortal && (
                          <span
                            className="text-[10px] font-medium uppercase tracking-wide flex-shrink-0"
                            style={{
                              color: isPublished
                                ? 'var(--color-success)'
                                : 'var(--color-danger)',
                            }}
                          >
                            {isPublished ? 'Published' : 'Draft'}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] num text-ink-2">
                        <LinkedUpdateIcon />
                        <span>{formatShortDate(u.event_timestamp)}</span>
                        <span className="text-ink-3">·</span>
                        <span>{u.log_type}</span>
                      </div>
                      {u.published_summary || (!isPortal && u.private_entry) ? (
                        <p className="mt-1.5 text-xs text-ink whitespace-pre-wrap line-clamp-2 leading-snug">
                          {u.published_summary || u.private_entry}
                        </p>
                      ) : null}
                    </>
                  );
                  return (
                    <li key={u.id}>
                      {isPortal ? (
                        <div
                          className="w-full text-left rounded-md border px-3 py-2"
                          style={{ background: bg, borderColor: border }}
                        >
                          {cardInner}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setLinkedUpdateModalEntry(u)}
                          className="w-full text-left rounded-md border px-3 py-2 hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                          style={{ background: bg, borderColor: border }}
                          title="Open this linked update in the compose modal"
                        >
                          {cardInner}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

          {/* D-111 — "+ note" stays as a quick-jot entry point on every
              task row, but opens the same compose modal the full Client
              Updates flow uses, pre-loaded with this task's phase + task
              id. The old inline-textarea quote-block render was removed;
              notes on a task now render as the linked-update card above
              this comment (same visual everywhere). One note format,
              one schema. D-121 — hidden in portal mode (no note
              authoring on the client side). */}
          {!isPortal && (
            <button
              type="button"
              onClick={() => setCreateNoteModalOpen(true)}
              className="mt-0.5 text-[11px] text-ink-3 hover:text-ink-2 underline decoration-dotted underline-offset-2 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
              title="Add a note linked to this task — opens the compose modal pre-loaded with this phase + task."
            >
              + note
            </button>
          )}
        </div>

          {/* D-099 — Critical toggle. Always visible (not hover-only) so
              the state is readable at a glance. Filled red flag when
              critical, outlined gray flag when not. One click toggles —
              works on every task, prebuilt or user-created.
              D-121 — hidden in portal mode. The CRITICAL badge attached
              to task_text above still surfaces (clients can see what's
              flagged critical), this toggle just isn't actionable on
              the client side. */}
          {!isPortal && (
            <button
              type="button"
              onClick={() => toggleCriticalMut.mutate(!isCritical)}
              disabled={toggleCriticalMut.isPending}
              className="mt-0.5 p-1 focus:outline-none focus:ring-1 focus:ring-accent rounded flex-shrink-0 transition-colors disabled:opacity-50"
              aria-label={isCritical ? 'Unmark as critical' : 'Mark as critical'}
              title={isCritical ? 'Unmark critical' : 'Mark critical'}
            >
              <svg
                viewBox="0 0 24 24"
                fill={isCritical ? 'var(--color-danger)' : 'none'}
                stroke={isCritical ? 'var(--color-danger)' : 'currentColor'}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`w-4 h-4 ${isCritical ? '' : 'text-ink-3 hover:text-ink'}`}
                aria-hidden
              >
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
            </button>
          )}

          {/* D-096 — task delete on the inline row. ✕ button is invisible
              at rest, fades in on row hover so it doesn't visually
              clutter the scan. Two-step confirm via ConfirmationDialog.
              D-121 — hidden in portal mode. */}
          {!isPortal && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-ink-3 hover:text-ink mt-0.5 text-sm leading-none px-1 focus:outline-none focus:ring-1 focus:ring-accent rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              aria-label={`Delete ${task.task_text}`}
              title="Delete task"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* D-121 — all interactive modals only mount in admin mode. In
          portal mode, the triggers above are hidden so these would
          never open anyway, but skipping the mount keeps the portal
          render tree free of Radix Dialog instances entirely. */}
      {!isPortal && (
        <>
          <ConfirmationDialog
            open={pendingComplete !== null}
            onOpenChange={(o) => {
              if (!o) setPendingComplete(null);
            }}
            title={
              pendingComplete
                ? `Mark "${task.task_text}" complete?`
                : `Mark "${task.task_text}" incomplete?`
            }
            description={
              pendingComplete
                ? 'Updates the phase progress and may transition the phase status. The action is logged in the audit trail. The completion date will stamp to today — you can backdate it after the fact by clicking the "Completed" tag.'
                : 'Reverts the task to incomplete. The phase progress will recompute and the completion date will clear.'
            }
            confirmLabel={pendingComplete ? 'Yes, mark complete' : 'Yes, mark incomplete'}
            cancelLabel="Cancel"
            variant="warning"
            pending={toggleMut.isPending}
            onConfirm={() => {
              if (pendingComplete !== null) {
                toggleMut.mutate(pendingComplete);
                setPendingComplete(null);
              }
            }}
          />

          <ConfirmationDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title={`Delete "${task.task_text}"?`}
            description="Removes this task from the phase entirely. The phase progress will recompute. The audit trail keeps a record of the deletion."
            confirmLabel="Yes, delete task"
            cancelLabel="Cancel"
            variant="danger"
            pending={deleteMut.isPending}
            onConfirm={() => {
              setConfirmDelete(false);
              deleteMut.mutate();
            }}
          />

          {/* D-109 — modal for the linked Client Update. Same compose
              modal everywhere else uses; opens in edit mode for the
              clicked entry. */}
          {linkedUpdateModalEntry && (
            <ClientUpdateComposeModal
              open={!!linkedUpdateModalEntry}
              onOpenChange={(o) => {
                if (!o) setLinkedUpdateModalEntry(null);
              }}
              projectId={projectId}
              mode={{ kind: 'edit', entry: linkedUpdateModalEntry }}
            />
          )}

          {/* D-111 — "+ note" quick-jot opens the same compose modal
              in create mode with phase + task pre-selected, so the
              user can post a one-line note without leaving the task
              view. */}
          <ClientUpdateComposeModal
            open={createNoteModalOpen}
            onOpenChange={setCreateNoteModalOpen}
            projectId={projectId}
            defaultPhaseId={phaseId}
            defaultTaskId={task.id}
            mode={{ kind: 'create' }}
          />
        </>
      )}
    </li>
  );
}

function LinkedUpdateIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={11}
      height={11}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="mt-0.5 flex-shrink-0"
      style={{ color: 'var(--color-ink-3)' }}
    >
      <path d="M6.5 9.5a2.5 2.5 0 0 0 3.5 0L13 6.5a2.5 2.5 0 0 0-3.5-3.5L8 4.5" />
      <path d="M9.5 6.5a2.5 2.5 0 0 0-3.5 0L3 9.5a2.5 2.5 0 0 0 3.5 3.5L8 11.5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// CompletedDateTag — green "Completed 5/07/26" pill on completed tasks.
// Click to edit (date input) so the user can backdate when a task was
// actually done if they marked it late. Submit Enter to save, Esc to
// cancel. (D-095)
// ---------------------------------------------------------------------------

function CompletedDateTag({
  completedDate,
  editing,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  pending,
  readOnly = false,
}: {
  completedDate: string | null;
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSubmitEdit: (iso: string) => void;
  pending: boolean;
  /** D-121 — portal mode renders the pill as a non-interactive label. */
  readOnly?: boolean;
}) {
  // Date input value — yyyy-mm-dd format expected by <input type="date">
  const initialYmd = completedDate ? completedDate.slice(0, 10) : '';
  const [draft, setDraft] = useState(initialYmd);

  if (readOnly) {
    return (
      <span
        className="text-sm num font-medium px-1 -mx-1"
        style={{ color: 'var(--color-success)' }}
      >
        Completed {formatShortDate(completedDate)}
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 text-xs">
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          className="text-xs border border-border rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (draft) onSubmitEdit(new Date(draft + 'T12:00:00').toISOString());
            } else if (e.key === 'Escape') {
              onCancelEdit();
            }
          }}
        />
        <button
          type="button"
          onClick={() => draft && onSubmitEdit(new Date(draft + 'T12:00:00').toISOString())}
          disabled={pending || !draft}
          className="text-xs px-1.5 py-0.5 rounded text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          style={{ background: 'var(--color-success)' }}
        >
          {pending ? '…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancelEdit}
          className="text-xs text-ink-3 hover:text-ink"
        >
          cancel
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onStartEdit}
      // D-100 — bumped from text-xs to text-sm to match the task title
      // size bump on inline rows.
      className="text-sm num font-medium hover:underline focus:outline-none focus:ring-1 focus:ring-accent rounded px-1 -mx-1"
      style={{ color: 'var(--color-success)' }}
      title="Click to backdate"
    >
      Completed {formatShortDate(completedDate)}
    </button>
  );
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, '0');
  const yr = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yr}`;
}

// ---------------------------------------------------------------------------
// InlineAddTaskForm — inline add-task form per task_group, used inside the
// expanded body on Overview. Mirrors the AddTaskForm pattern from PhasePage
// so the inline view has full feature parity (D-099).
// ---------------------------------------------------------------------------

function InlineAddTaskForm({
  phaseId,
  projectId,
  taskGroup,
  onDone,
}: {
  phaseId: string;
  projectId: string;
  taskGroup: string | null;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  // D-111 — `task_note` column dropped. The create form no longer carries
  // a Note field; notes on a task are now created via the "+ note"
  // affordance on the row (after the task exists), which opens the
  // compose modal with phase + task pre-loaded. One note format, one
  // surface — same vocabulary as full-compose Client Updates.
  const [critical, setCritical] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const createMut = useMutation({
    mutationFn: async () => {
      const trimmed = text.trim();
      if (!trimmed) throw new Error('Task text is required.');
      await createTask(phaseId, {
        task_text: trimmed,
        task_group: taskGroup,
        is_critical: critical,
      });
      await qc.invalidateQueries({ queryKey: ['tasks', 'phase', phaseId] });
      await qc.invalidateQueries({ queryKey: ['phases', 'one', phaseId] });
      await qc.invalidateQueries({ queryKey: ['projects', 'one', projectId] });
    },
    onSuccess: () => {
      setText('');
      setCritical(false);
      onDone();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        createMut.mutate();
      }}
      className="mt-2 rounded-md border p-3 space-y-2"
      style={{
        background: 'var(--color-card)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-3 num">
        <span>{taskGroup ?? 'Other'}</span>
        <span aria-hidden>·</span>
        <span>New task</span>
      </div>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1">
          Task title <span className="text-[var(--color-danger)]">*</span>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Pull final inspection"
          className="w-full border border-border rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <p className="text-[11px] text-ink-3 italic">
        Use <span className="font-medium not-italic text-ink-2">+ note</span>{' '}
        on the task row after creating it to attach a Client Update with
        notes, AI drafter, date, urgent flag, etc.
      </p>
      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <label className="flex items-center gap-1.5 text-sm text-ink-2 cursor-pointer">
          <input
            type="checkbox"
            checked={critical}
            onChange={(e) => setCritical(e.target.checked)}
            className="w-4 h-4 accent-[var(--color-danger)]"
          />
          Mark as critical
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDone}
            className="px-3 py-1.5 rounded text-sm border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMut.isPending || !text.trim()}
            className="px-3 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            style={{ background: 'var(--color-accent)' }}
          >
            {createMut.isPending ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </div>
      {createMut.isError && (
        <p className="text-xs num" style={{ color: 'var(--color-danger)' }}>
          {(createMut.error as Error).message}
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// NewGroupAdder — "+ Add task in a new group" affordance at the bottom of
// the expanded body. Two-step flow under D-101 (was one-step but had a
// focus-steal bug — InlineAddTaskForm's autoFocus useEffect fired on
// first keystroke into the group-name input, redirecting subsequent
// keystrokes to the task-text input. Now the group-name step explicitly
// "confirms" via Enter or a Continue button before the task form mounts).
// ---------------------------------------------------------------------------

function NewGroupAdder({
  phaseId,
  projectId,
  existingGroups,
}: {
  phaseId: string;
  projectId: string;
  existingGroups: string[];
}) {
  type Step = 'closed' | 'name' | 'task';
  const [step, setStep] = useState<Step>('closed');
  const [groupName, setGroupName] = useState('');
  const trimmed = groupName.trim();
  const isDuplicate = existingGroups.includes(trimmed);
  const canContinue = trimmed.length > 0 && !isDuplicate;

  if (step === 'closed') {
    return (
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setStep('name')}
          className="text-sm underline hover:no-underline focus:outline-none focus:ring-1 focus:ring-accent rounded"
          style={{ color: 'var(--color-accent)' }}
        >
          + Add task in a new group
        </button>
      </div>
    );
  }

  if (step === 'name') {
    return (
      <div
        className="pt-1 mt-2 rounded-md border p-3 space-y-2"
        style={{
          background: 'var(--color-card)',
          borderColor: 'var(--color-border)',
        }}
      >
        <p className="text-[11px] uppercase tracking-wider text-ink-3 num">
          Step 1 of 2 · Name the new group
        </p>
        <input
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canContinue) {
              e.preventDefault();
              setStep('task');
            } else if (e.key === 'Escape') {
              setStep('closed');
              setGroupName('');
            }
          }}
          placeholder="New group name (e.g. Special Inspections)"
          autoFocus
          className="w-full border border-border rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {isDuplicate && (
          <p className="text-xs italic" style={{ color: 'var(--color-warning)' }}>
            A group with that name already exists in this phase.
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setStep('closed');
              setGroupName('');
            }}
            className="px-3 py-1.5 rounded text-xs border border-border bg-white hover:bg-[var(--color-surface-2)] text-ink-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canContinue && setStep('task')}
            disabled={!canContinue}
            className="px-3 py-1.5 rounded text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            style={{ background: 'var(--color-accent)' }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // step === 'task' — group name confirmed, now show the task creation form
  return (
    <div className="pt-1 space-y-1">
      <p className="text-[11px] uppercase tracking-wider text-ink-3 num">
        Step 2 of 2 · Add the first task to{' '}
        <span className="font-semibold text-ink">{trimmed}</span>
      </p>
      <InlineAddTaskForm
        phaseId={phaseId}
        projectId={projectId}
        taskGroup={trimmed}
        onDone={() => {
          setStep('closed');
          setGroupName('');
        }}
      />
    </div>
  );
}

function CalendarPencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M14 17l-2 2 1 3 3-1 5-5-3-3z" />
    </svg>
  );
}

/**
 * D-120 — Chevron expand/collapse icon for the PhaseRow's dedicated
 * left column. Rotates 90° clockwise when expanded so the same triangle
 * does double-duty: points right when collapsed ("click to open"), points
 * down when expanded ("currently open"). 16px stroke-2 triangle has real
 * presence — the previous 12px text glyph was easy to miss.
 */
function ChevronExpandIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-transform duration-200"
      style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
      aria-hidden
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PhaseStatusPill — tinted pill matching the standard primitive
// ---------------------------------------------------------------------------

function PhaseStatusPill({ status }: { status: string }) {
  const style = phaseStatusPillStyle(status);
  const dotColor = phaseStatusColor(status);
  return (
    <span
      className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border"
      style={style}
    >
      <span
        aria-hidden
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: dotColor }}
      />
      <span style={{ minWidth: 70, textAlign: 'left' }}>{status}</span>
    </span>
  );
}

function phaseStatusColor(status: string): string {
  switch (status) {
    case 'Complete':
      return 'var(--color-success)';
    case 'In Progress':
      return 'var(--color-warning)';
    case 'Not Started':
    default:
      return 'var(--color-ink-3)';
  }
}

function phaseStatusPillStyle(status: string): React.CSSProperties {
  const c = phaseStatusColor(status);
  switch (status) {
    case 'Complete':
      return {
        background: 'var(--color-success-soft)',
        borderColor: 'var(--color-success)',
        color: c,
      };
    case 'In Progress':
      return {
        background: 'var(--color-warning-soft)',
        borderColor: 'var(--color-warning)',
        color: c,
      };
    case 'Not Started':
    default:
      return {
        background: 'var(--color-surface-2)',
        borderColor: 'var(--color-border-strong)',
        color: c,
      };
  }
}

// ---------------------------------------------------------------------------
// ProgressBar — slim full-width bar, matches phase status color when
// any progress exists; faint neutral when at 0
// ---------------------------------------------------------------------------

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  const fill =
    clamped >= 100
      ? 'var(--color-success)'
      : clamped > 0
        ? 'var(--color-warning)'
        : 'var(--color-border)';
  // D-119 — bumped from h-1.5 (6px) to h-3 (12px). The original was so
  // thin it read as decoration; the new height + the inner shadow on the
  // track give it the weight C wanted: "more serious and more powerful
  // feeling." Track border on top edge plus an inner shadow give the
  // illusion of depth so the fill reads as deliberately filling a
  // recessed channel.
  return (
    <div
      className="flex-1 h-3 rounded-full overflow-hidden relative"
      style={{
        background: 'var(--color-surface-2)',
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.12)',
      }}
      aria-label={`${clamped}% complete`}
    >
      <div
        style={{
          width: `${clamped}%`,
          height: '100%',
          background: fill,
          transition: 'width 0.2s ease',
          // Subtle highlight on the top edge of the fill so it has
          // depth instead of reading as a flat color block.
          boxShadow:
            clamped > 0
              ? 'inset 0 1px 0 rgba(255, 255, 255, 0.25)'
              : 'none',
        }}
      />
    </div>
  );
}

function prettyType(t: string): string {
  return t
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
